import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const CookiePolicy = () => {
  const { t } = useTranslation();

  // Add modern CSS animations
  useEffect(() => {
    const styleId = 'cookie-policy-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }
        
        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-10px) rotate(5deg);
          }
        }
        
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        
        .cookie-policy-container {
          animation: fadeInUp 0.8s ease-out;
        }
        
        .cookie-policy-header {
          animation: slideInLeft 1s ease-out 0.2s both;
        }
        
        .cookie-policy-section {
          animation: fadeInUp 0.6s ease-out both;
        }
        
        .cookie-policy-section:nth-child(1) { animation-delay: 0.3s; }
        .cookie-policy-section:nth-child(2) { animation-delay: 0.4s; }
        .cookie-policy-section:nth-child(3) { animation-delay: 0.5s; }
        .cookie-policy-section:nth-child(4) { animation-delay: 0.6s; }
        .cookie-policy-section:nth-child(5) { animation-delay: 0.7s; }
        .cookie-policy-section:nth-child(6) { animation-delay: 0.8s; }
        
        .floating-element {
          animation: float 3s ease-in-out infinite;
        }
        
        .pulse-element {
          animation: pulse 2s ease-in-out infinite;
        }
        
        .shimmer-effect {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
          background-size: 200% 100%;
          animation: shimmer 2s infinite;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <div 
      className="cookie-policy-container"
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #f5576c 75%, #4facfe 100%)',
        backgroundSize: '400% 400%',
        animation: 'gradientShift 15s ease infinite',
        padding: '3rem 1rem',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Floating decorative elements */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '5%',
        width: '100px',
        height: '100px',
        background: 'rgba(255,255,255,0.1)',
        borderRadius: '50%',
        animation: 'float 4s ease-in-out infinite'
      }} />
      <div style={{
        position: 'absolute',
        top: '20%',
        right: '10%',
        width: '80px',
        height: '80px',
        background: 'rgba(255,255,255,0.08)',
        borderRadius: '50%',
        animation: 'float 3s ease-in-out infinite 1s'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '15%',
        left: '8%',
        width: '60px',
        height: '60px',
        background: 'rgba(255,255,255,0.06)',
        borderRadius: '50%',
        animation: 'float 5s ease-in-out infinite 2s'
      }} />
      
      <div className="max-w-4xl mx-auto">
        {/* Header moderne */}
        <div className="cookie-policy-header text-center mb-12">
          <div 
            className="pulse-element"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #fff 0%, #f8fafc 100%)',
              borderRadius: '50%',
              marginBottom: '2rem',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.1)',
              border: '4px solid rgba(255,255,255,0.2)'
            }}
          >
            <span style={{ fontSize: '2rem' }}>🍪</span>
          </div>
          
          <h1 style={{
            fontSize: '3.5rem',
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #fff 0%, #f1f5f9 50%, #e2e8f0 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: '1.5rem',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)',
            letterSpacing: '-0.02em'
          }}>
            {t('cookiePolicy.title', 'Politique de Cookies')}
          </h1>
          
          <p style={{
            fontSize: '1.25rem',
            color: 'rgba(255,255,255,0.9)',
            maxWidth: '32rem',
            margin: '0 auto',
            lineHeight: 1.6,
            fontWeight: 500
          }}>
            {t('cookiePolicy.subtitle', 'Découvrez comment nous utilisons les cookies pour améliorer votre expérience sur Nodemented.')}
          </p>
        </div>

        {/* Contenu principal */}
        <div style={{
          background: 'rgba(255,255,255,0.95)',
          borderRadius: '24px',
          boxShadow: '0 32px 64px rgba(0,0,0,0.2)',
          border: '1px solid rgba(255,255,255,0.2)',
          overflow: 'hidden',
          backdropFilter: 'blur(20px)',
          position: 'relative'
        }}>
          {/* Barre décorative animée */}
          <div style={{
            height: '6px',
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #f5576c 75%, #4facfe 100%)',
            backgroundSize: '400% 100%',
            animation: 'shimmer 3s ease-in-out infinite'
          }} />
          
          {/* Effet de brillance */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
            animation: 'shimmer 4s ease-in-out infinite',
            pointerEvents: 'none'
          }} />
          
          <div style={{ padding: '3rem' }}>
            {/* Date de mise à jour */}
            <div 
              className="cookie-policy-section"
              style={{
                marginBottom: '2rem',
                padding: '1rem 1.5rem',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%)',
                borderRadius: '16px',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: 'linear-gradient(90deg, #3b82f6, #9333ea)',
                animation: 'shimmer 2s ease-in-out infinite'
              }} />
              <p style={{
                fontSize: '0.875rem',
                color: '#1e40af',
                fontWeight: 500,
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  background: '#3b82f6',
                  borderRadius: '50%',
                  animation: 'pulse 2s infinite'
                }} />
                {t('cookiePolicy.lastUpdated', 'Dernière mise à jour : 12 septembre 2025')}
              </p>
            </div>

            {/* Section 1: Introduction */}
            <section className="cookie-policy-section" style={{ marginBottom: '2.5rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 16px rgba(16, 185, 129, 0.3)',
                  animation: 'pulse 3s infinite'
                }}>
                  <span style={{ color: 'white', fontSize: '1.25rem', fontWeight: 'bold' }}>1</span>
                </div>
                <h2 style={{
                  fontSize: '1.875rem',
                  fontWeight: 'bold',
                  color: '#1f2937',
                  margin: 0,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  {t('cookiePolicy.introduction.title', 'Introduction')}
                </h2>
              </div>
              <div style={{
                padding: '1.5rem',
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%)',
                borderRadius: '16px',
                border: '1px solid rgba(16, 185, 129, 0.1)',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '4px',
                  height: '100%',
                  background: 'linear-gradient(180deg, #10b981, #059669)',
                  borderRadius: '2px'
                }} />
                <div style={{ color: '#374151', lineHeight: 1.7, fontSize: '1.125rem' }}>
                  <p style={{ marginBottom: '1rem' }}>
                    {t('cookiePolicy.introduction.content1', 'Chez Nodemented, nous utilisons des cookies et des technologies similaires pour vous offrir la meilleure expérience possible. Cette politique explique comment et pourquoi nous utilisons ces technologies.')}
                  </p>
                  <p style={{ margin: 0 }}>
                    {t('cookiePolicy.introduction.content2', 'Les cookies sont de petits fichiers texte stockés sur votre appareil qui nous aident à améliorer nos services, analyser les performances et personnaliser votre expérience.')}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 2: Types de cookies */}
            <section className="cookie-policy-section" style={{ marginBottom: '2.5rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 16px rgba(59, 130, 246, 0.3)',
                  animation: 'pulse 3s infinite 0.5s'
                }}>
                  <span style={{ color: 'white', fontSize: '1.25rem', fontWeight: 'bold' }}>2</span>
                </div>
                <h2 style={{
                  fontSize: '1.875rem',
                  fontWeight: 'bold',
                  color: '#1f2937',
                  margin: 0,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  {t('cookiePolicy.types.title', 'Types de Cookies')}
                </h2>
              </div>

              <div style={{ display: 'grid', gap: '1.5rem' }}>
                {/* Cookies essentiels */}
                <div 
                  className="element"
                  style={{
                    padding: '1.5rem',
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.15) 100%)',
                    borderRadius: '16px',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-4px)';
                    e.target.style.boxShadow = '0 20px 40px rgba(16, 185, 129, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(90deg, #10b981, #059669)',
                    animation: 'shimmer 2s ease-in-out infinite'
                  }} />
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    <span style={{ fontSize: '1.5rem' }}>🔒</span>
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      color: '#065f46',
                      margin: 0
                    }}>
                      {t('cookiePolicy.essential.title', 'Cookies Essentiels')}
                    </h3>
                  </div>
                  <p style={{
                    color: '#047857',
                    marginBottom: '0.75rem',
                    lineHeight: 1.6
                  }}>
                    {t('cookiePolicy.essential.description', 'Ces cookies sont nécessaires au fonctionnement du site et ne peuvent pas être désactivés.')}
                  </p>
                  <ul style={{
                    fontSize: '0.875rem',
                    color: '#059669',
                    listStyle: 'none',
                    padding: 0,
                    margin: 0
                  }}>
                    <li style={{ marginBottom: '0.25rem' }}>• {t('cookiePolicy.essential.item1', 'Authentification et sécurité des sessions')}</li>
                    <li style={{ marginBottom: '0.25rem' }}>• {t('cookiePolicy.essential.item2', 'Préférences de langue et région')}</li>
                    <li>• {t('cookiePolicy.essential.item3', 'Panier et données de session')}</li>
                  </ul>
                </div>

                {/* Cookies d'analyse */}
                <div 
                  className="element"
                  style={{
                    padding: '1.5rem',
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(29, 78, 216, 0.15) 100%)',
                    borderRadius: '16px',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    animationDelay: '0.5s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-4px)';
                    e.target.style.boxShadow = '0 20px 40px rgba(59, 130, 246, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(90deg, #3b82f6, #1d4ed8)',
                    animation: 'shimmer 2s ease-in-out infinite 0.5s'
                  }} />
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    <span style={{ fontSize: '1.5rem' }}>📊</span>
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      color: '#1e40af',
                      margin: 0
                    }}>
                      {t('cookiePolicy.analytics.title', 'Cookies d\'Analyse')}
                    </h3>
                  </div>
                  <p style={{
                    color: '#1e40af',
                    marginBottom: '0.75rem',
                    lineHeight: 1.6
                  }}>
                    {t('cookiePolicy.analytics.description', 'Ces cookies nous aident à comprendre comment vous utilisez notre site pour l\'améliorer.')}
                  </p>
                  <ul style={{
                    fontSize: '0.875rem',
                    color: '#2563eb',
                    listStyle: 'none',
                    padding: 0,
                    margin: 0
                  }}>
                    <li style={{ marginBottom: '0.25rem' }}>• {t('cookiePolicy.analytics.item1', 'Statistiques de visite anonymes')}</li>
                    <li style={{ marginBottom: '0.25rem' }}>• {t('cookiePolicy.analytics.item2', 'Analyse des performances')}</li>
                    <li>• {t('cookiePolicy.analytics.item3', 'Optimisation de l\'expérience utilisateur')}</li>
                  </ul>
                </div>

                {/* Cookies marketing */}
                <div 
                  className="element"
                  style={{
                    padding: '1.5rem',
                    background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(126, 34, 206, 0.15) 100%)',
                    borderRadius: '16px',
                    border: '1px solid rgba(147, 51, 234, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    animationDelay: '1s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-4px)';
                    e.target.style.boxShadow = '0 20px 40px rgba(147, 51, 234, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(90deg, #9333ea, #7e22ce)',
                    animation: 'shimmer 2s ease-in-out infinite 1s'
                  }} />
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    <span style={{ fontSize: '1.5rem' }}>🎯</span>
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      color: '#7e22ce',
                      margin: 0
                    }}>
                      {t('cookiePolicy.marketing.title', 'Cookies Marketing')}
                    </h3>
                  </div>
                  <p style={{
                    color: '#7e22ce',
                    marginBottom: '0.75rem',
                    lineHeight: 1.6
                  }}>
                    {t('cookiePolicy.marketing.description', 'Ces cookies permettent de personnaliser les publicités et contenus selon vos intérêts.')}
                  </p>
                  <ul style={{
                    fontSize: '0.875rem',
                    color: '#9333ea',
                    listStyle: 'none',
                    padding: 0,
                    margin: 0
                  }}>
                    <li style={{ marginBottom: '0.25rem' }}>• {t('cookiePolicy.marketing.item1', 'Publicités personnalisées')}</li>
                    <li style={{ marginBottom: '0.25rem' }}>• {t('cookiePolicy.marketing.item2', 'Recommandations de contenu')}</li>
                    <li>• {t('cookiePolicy.marketing.item3', 'Suivi des conversions')}</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 3: Gestion des cookies */}
            <section className="cookie-policy-section" style={{ marginBottom: '2.5rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #9333ea 0%, #7e22ce 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 16px rgba(147, 51, 234, 0.3)',
                  animation: 'pulse 3s infinite 1s'
                }}>
                  <span style={{ color: 'white', fontSize: '1.25rem', fontWeight: 'bold' }}>3</span>
                </div>
                <h2 style={{
                  fontSize: '1.875rem',
                  fontWeight: 'bold',
                  color: '#1f2937',
                  margin: 0,
                  background: 'linear-gradient(135deg, #9333ea 0%, #7e22ce 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  {t('cookiePolicy.management.title', 'Gestion de vos Préférences')}
                </h2>
              </div>
              <div style={{
                padding: '1.5rem',
                background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.05) 0%, rgba(126, 34, 206, 0.05) 100%)',
                borderRadius: '16px',
                border: '1px solid rgba(147, 51, 234, 0.1)',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '4px',
                  height: '100%',
                  background: 'linear-gradient(180deg, #9333ea, #7e22ce)',
                  borderRadius: '2px'
                }} />
                <div style={{ color: '#374151', lineHeight: 1.7, fontSize: '1.125rem' }}>
                  <p style={{ marginBottom: '1rem' }}>
                    {t('cookiePolicy.management.content1', 'Vous avez le contrôle total sur vos préférences de cookies. Vous pouvez modifier vos choix à tout moment en :')}
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: '#6b7280' }}>
                    <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ color: '#9333ea', fontSize: '1.25rem' }}>•</span>
                      {t('cookiePolicy.management.method1', 'Utilisant notre bannière de consentement lors de votre première visite')}
                    </li>
                    <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ color: '#9333ea', fontSize: '1.25rem' }}>•</span>
                      {t('cookiePolicy.management.method2', 'Accédant aux paramètres de cookies dans le pied de page')}
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ color: '#9333ea', fontSize: '1.25rem' }}>•</span>
                      {t('cookiePolicy.management.method3', 'Configurant directement votre navigateur web')}
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 4: Durée de conservation */}
            <section className="cookie-policy-section" style={{ marginBottom: '2.5rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 16px rgba(245, 158, 11, 0.3)',
                  animation: 'pulse 3s infinite 1.5s'
                }}>
                  <span style={{ color: 'white', fontSize: '1.25rem', fontWeight: 'bold' }}>4</span>
                </div>
                <h2 style={{
                  fontSize: '1.875rem',
                  fontWeight: 'bold',
                  color: '#1f2937',
                  margin: 0,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  {t('cookiePolicy.retention.title', 'Durée de Conservation')}
                </h2>
              </div>
              <div style={{
                padding: '1.5rem',
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.15) 100%)',
                borderRadius: '16px',
                border: '1px solid rgba(245, 158, 11, 0.2)',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #f59e0b, #d97706)',
                  animation: 'shimmer 2s ease-in-out infinite 1.5s'
                }} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div style={{ textAlign: 'center', padding: '1rem' }}>
                    <div style={{
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      color: '#d97706',
                      marginBottom: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ fontSize: '1.5rem' }}>⏱️</span>
                      Session
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#92400e' }}>
                      {t('cookiePolicy.retention.session', 'Cookies de session')}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '1rem' }}>
                    <div style={{
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      color: '#d97706',
                      marginBottom: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ fontSize: '1.5rem' }}>📊</span>
                      30 jours
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#92400e' }}>
                      {t('cookiePolicy.retention.analytics', 'Cookies d\'analyse')}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '1rem' }}>
                    <div style={{
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      color: '#d97706',
                      marginBottom: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ fontSize: '1.5rem' }}>⚙️</span>
                      1 an
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#92400e' }}>
                      {t('cookiePolicy.retention.preferences', 'Préférences utilisateur')}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 5: Vos droits */}
            <section className="cookie-policy-section" style={{ marginBottom: '2.5rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 16px rgba(236, 72, 153, 0.3)',
                  animation: 'pulse 3s infinite 2s'
                }}>
                  <span style={{ color: 'white', fontSize: '1.25rem', fontWeight: 'bold' }}>5</span>
                </div>
                <h2 style={{
                  fontSize: '1.875rem',
                  fontWeight: 'bold',
                  color: '#1f2937',
                  margin: 0,
                  background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  {t('cookiePolicy.rights.title', 'Vos Droits')}
                </h2>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                <div style={{
                  padding: '1rem',
                  background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.05) 0%, rgba(219, 39, 119, 0.1) 100%)',
                  borderRadius: '12px',
                  border: '1px solid rgba(236, 72, 153, 0.2)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}>
                  <h4 style={{
                    fontWeight: 600,
                    color: '#be185d',
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    ✅ {t('cookiePolicy.rights.consent', 'Droit de consentement')}
                  </h4>
                  <p style={{ fontSize: '0.875rem', color: '#9f1239', margin: 0 }}>
                    {t('cookiePolicy.rights.consentDesc', 'Choisir librement quels cookies accepter')}
                  </p>
                </div>
                <div style={{
                  padding: '1rem',
                  background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.05) 0%, rgba(219, 39, 119, 0.1) 100%)',
                  borderRadius: '12px',
                  border: '1px solid rgba(236, 72, 153, 0.2)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}>
                  <h4 style={{
                    fontWeight: 600,
                    color: '#be185d',
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    🔄 {t('cookiePolicy.rights.withdraw', 'Droit de retrait')}
                  </h4>
                  <p style={{ fontSize: '0.875rem', color: '#9f1239', margin: 0 }}>
                    {t('cookiePolicy.rights.withdrawDesc', 'Retirer votre consentement à tout moment')}
                  </p>
                </div>
                <div style={{
                  padding: '1rem',
                  background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.05) 0%, rgba(219, 39, 119, 0.1) 100%)',
                  borderRadius: '12px',
                  border: '1px solid rgba(236, 72, 153, 0.2)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}>
                  <h4 style={{
                    fontWeight: 600,
                    color: '#be185d',
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    📋 {t('cookiePolicy.rights.information', 'Droit d\'information')}
                  </h4>
                  <p style={{ fontSize: '0.875rem', color: '#9f1239', margin: 0 }}>
                    {t('cookiePolicy.rights.informationDesc', 'Être informé de l\'utilisation des cookies')}
                  </p>
                </div>
                <div style={{
                  padding: '1rem',
                  background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.05) 0%, rgba(219, 39, 119, 0.1) 100%)',
                  borderRadius: '12px',
                  border: '1px solid rgba(236, 72, 153, 0.2)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}>
                  <h4 style={{
                    fontWeight: 600,
                    color: '#be185d',
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    ⚙️ {t('cookiePolicy.rights.control', 'Droit de contrôle')}
                  </h4>
                  <p style={{ fontSize: '0.875rem', color: '#9f1239', margin: 0 }}>
                    {t('cookiePolicy.rights.controlDesc', 'Contrôler vos préférences facilement')}
                  </p>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="cookie-policy-section" style={{ marginBottom: '2.5rem' }}>
              <div style={{
                padding: '2rem',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 51, 234, 0.08) 100%)',
                borderRadius: '20px',
                border: '2px solid rgba(59, 130, 246, 0.2)',
                position: 'relative',
                textAlign: 'center'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-15px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '30px',
                  height: '30px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #9333ea 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'float 3s ease-in-out infinite'
                }}>
                  <span style={{ color: 'white', fontSize: '1rem' }}>📞</span>
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: '#1f2937',
                  marginBottom: '1rem',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #9333ea 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.25rem' }}>📞</span>
                  {t('cookiePolicy.contact.title', 'Nous Contacter')}
                </h3>
                <p style={{
                  fontSize: '1.125rem',
                  color: '#4b5563',
                  marginBottom: '1.5rem',
                  fontWeight: 500
                }}>
                  {t('cookiePolicy.contact.description', 'Pour toute question concernant cette politique de cookies ou vos données personnelles, contactez-nous :')}
                </p>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  maxWidth: '400px',
                  margin: '0 auto'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1rem',
                    background: 'white',
                    borderRadius: '12px',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    boxShadow: '0 4px 8px rgba(59, 130, 246, 0.1)',
                    transition: 'all 0.3s ease'
                  }}>
                    <span style={{ fontSize: '1.5rem', animation: 'pulse 2s infinite' }}>📧</span>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem' }}>Email</div>
                      <div style={{ color: '#3b82f6', fontWeight: 500 }}>nodemented.contact@gmail.com</div>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1rem',
                    background: 'white',
                    borderRadius: '12px',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    boxShadow: '0 4px 8px rgba(59, 130, 246, 0.1)',
                    transition: 'all 0.3s ease'
                  }}>
                    <span style={{ fontSize: '1.5rem', animation: 'pulse 2s infinite 0.5s' }}>🌐</span>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem' }}>Site</div>
                      <div style={{ color: '#3b82f6', fontWeight: 500 }}>www.nodemented.com</div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Footer décoratif */}
          <div style={{
            textAlign: 'center',
            padding: '2rem 0',
            borderTop: '1px solid rgba(156, 163, 175, 0.2)',
            marginTop: '3rem'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              color: '#6b7280',
              animation: 'slideInLeft 1s ease-out'
            }}>
              <span style={{ fontSize: '1.25rem', animation: 'float 3s ease-in-out infinite' }}>🍪</span>
              {t('cookiePolicy.footer', 'Merci de votre confiance dans notre gestion des cookies !')}
              <span style={{ fontSize: '1.25rem', animation: 'float 3s ease-in-out infinite 1s' }}>✨</span>
            </div>
          </div>
        </div>

        {/* Footer avec boutons d'action */}
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <button 
            onClick={() => window.location.href = '/'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '1rem 2rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              fontWeight: 600,
              fontSize: '1.125rem',
              borderRadius: '16px',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.3)';
            }}
          >
            <span style={{ fontSize: '1.25rem' }}>←</span>
            {t('cookiePolicy.backButton', 'Retour au site')}
          </button>
        </div>
      </div>
      
      {/* Add gradient animation keyframes */}
      <style jsx>{`
        @keyframes gradientShift {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
      `}</style>
    </div>
  );
};

export default CookiePolicy;
